/**
 * feat-317 — per-role scoring grids (ADR feat-317 §6, §7.1).
 *
 * Every scorecard response gains an additive `groups[]`: one element per
 * EFFECTIVE ROLE in the data set, already in display order (D7, sorted by the
 * backend). The frontend renders it VERBATIM:
 *
 *   - never re-sorts groups, never regroups rows, never merges grids by
 *     column set (spec "Accepted consequence");
 *   - an absent or empty `groups` (old backend, deploy skew §6.7) synthesises
 *     ONE legacy group from today's top-level fields, so the surface renders
 *     exactly as it did before this feature;
 *   - headings render iff more than one group is VISIBLE (D6). One group is
 *     today's markup with no heading and no wrapper.
 *
 * Pure: no React, no arithmetic on scores.
 */

/** G4 — the header every group carries ahead of its legacy-shaped fields. */
export interface GroupHeader {
  /** Canonical `prompt_template.role`, or null (no active version / synthesised). */
  role: string | null;
  /** `tenant_role.display_name` (falls back to role), or null. The grid heading. */
  display_name: string | null;
  /** The role's active tenant-level version (QA/debugging; never rendered). */
  prompt_version_id?: string | null;
}

/**
 * `resp.groups` when present and non-empty, verbatim and in order. Otherwise
 * ONE group synthesised by `legacy` from the top-level fields (old backend).
 */
export function groupsOf<R extends { groups?: G[] | null }, G extends GroupHeader>(
  resp: R,
  legacy: (r: R) => G,
): G[] {
  if (Array.isArray(resp.groups) && resp.groups.length > 0) {
    return resp.groups;
  }
  return [legacy(resp)];
}

/**
 * Apply a surface's EXISTING client-side filter (manager, month, location
 * scope…) to every group, then drop groups left with no rows. If every group
 * empties, return just the first one (emptied) so today's empty state renders
 * with its columns. Order is preserved; nothing is re-sorted.
 */
export function visibleGroups<G>(
  groups: G[],
  rowsOf: (g: G) => unknown[],
  filter: (g: G) => G = (g) => g,
): G[] {
  const filtered = groups.map(filter);
  const kept = filtered.filter((g) => rowsOf(g).length > 0);
  if (kept.length > 0) return kept;
  return filtered.length > 0 ? [filtered[0]] : [];
}

/** D6 — role headings render only when more than one grid is on screen. */
export function showRoleHeadings(groups: readonly unknown[]): boolean {
  return groups.length > 1;
}
